import { createConsoleSink } from './console-sink';
import { createMonitoring } from './create-monitoring';
import { createDedupe } from './dedupe';
import { buildConfig } from './env-config';
import { installGlobalHandlers } from './global-handlers';
import {
  SinkName,
  type MonitoringConfig,
  type MonitoringProperties,
  type MonitoringSink,
} from './types';

/**
 * Builds the sinks named in the config.
 *
 * The only place that maps a name to an implementation, and the only place a
 * new destination has to be registered. A sink that cannot be built is skipped
 * rather than fatal, so one misconfigured destination does not take the others
 * down with it.
 */
const createSinks = (config: MonitoringConfig): MonitoringSink[] =>
  config.sinks.flatMap((name) => {
    switch (name) {
      case SinkName.Console:
        return [createConsoleSink()];
      default:
        return [];
    }
  });

/**
 * The app's monitoring instance, initialised **at import time**.
 *
 * `src/index.tsx` imports this module for its side effect, first. A call to an
 * `init()` function would not do: ES modules evaluate every import before any
 * statement, so a call at the top of `index.tsx` still runs after the module
 * graph — by which point the module-scope throws in `@consigli/hooks`' config,
 * `./app`'s `getMsalInstance()` and the missing-root check have already
 * happened. All three blank the page and none is reachable from a boundary.
 *
 * NOTE: because this runs at import time, importing `@/monitoring` anywhere —
 * unit tests included — calls `buildConfig`. That is harmless only because
 * every committed `.env` leaves `VITE_MONITORING_SINKS` blank, so no sink is
 * built. Vitest runs in mode `test` and loads `.env.test`; keep it blank there.
 */
const monitoring = createMonitoring({
  loadConfig: () => buildConfig(import.meta.env),
  createSinks,
});

export const { reportError, setUser, flush } = monitoring;

const dedupe = createDedupe();

/**
 * The entry point for *automatic* capture — the boundary, the error element and
 * the global handlers. An explicit `reportError()` from a `catch` block bypasses
 * the dedupe: the caller chose to report, so a stale fingerprint must not silence it.
 */
export const captureError = (error: unknown, properties?: MonitoringProperties): void => {
  if (!dedupe.claim(error)) {
    return;
  }
  reportError(error, properties);
};

// Only when something will actually receive the reports, so a disabled build
// does not touch `window`.
const teardownGlobalHandlers =
  monitoring.sinks.length > 0 ? installGlobalHandlers(captureError) : undefined;

/** Names of the sinks actually built. Empty means monitoring is off. */
export const activeSinks = monitoring.sinks;

// So a developer can tell "off" from "broken" without opening devtools.
if (import.meta.env.DEV) {
  console.info(
    monitoring.sinks.length > 0
      ? `[monitoring] sinks: ${monitoring.sinks.join(', ')}`
      : '[monitoring] off - VITE_MONITORING_SINKS is blank.',
  );
}

// HMR would otherwise leave a second set of sinks and a duplicate pair of
// window listeners attached.
import.meta.hot?.dispose(() => {
  teardownGlobalHandlers?.();
  monitoring.dispose();
});
